import Axios from 'axios';
import mixpanel from 'mixpanel-browser';

import { API } from 'consts';
import { analytics as analyticsBrowser } from 'segmentAnalytics';

import { ensureSession, registerSessionMiddleware } from './sessionTracking';

const BASE_URL = `${API.URL}/${API.VERSION}`;

let sessionMiddlewareRegistered = false;

// Guards against duplicate identify/group calls. The identify effect can fire
// more than once as the auth token settles on load (rehydrate -> refresh),
// which previously produced two identical identify + group events. Keyed on
// token + platform so a genuine platform switch (admin <-> advocate) still
// re-identifies and sets the other capability flag.
let lastIdentifyKey: string | null = null;

async function addUserProperties(authObj: any): Promise<any> {
  const identifyKey = `${authObj?.token ?? ''}::${authObj?.platform ?? ''}`;
  if (identifyKey === lastIdentifyKey) {
    return;
  }
  lastIdentifyKey = identifyKey;

  let getUserById: any;
  try {
    getUserById = await Axios.get(`${BASE_URL}/advocate/getUserById`, {
      headers: { Authorization: `Bearer ${authObj.token}` },
    });
  } catch (e) {
    lastIdentifyKey = null;
    throw e;
  }
  const profile = getUserById?.data?.data?.profile;
  const platform = authObj?.platform;

  let session: string;
  let persona: string;

  const isAdminPlatform = platform === 'EMPLOYER' || platform === 'RECRUITER';

  if (platform === 'EMPLOYER') {
    session = 'EB';
    persona = 'admin';
  } else if (platform === 'RECRUITER') {
    session = 'RE';
    persona = 'admin';
  } else {
    session = 'BA';
    persona = 'contributor_advocate';
  }

  const userInfo = {
    Email: profile?.email?.toLowerCase(),
    Name: `${profile?.firstName} ${profile?.lastName}`,
    Session: session,
    Company: profile?.company_name,
    Id: profile?.user_id?.split('-')[0],
    CompanyId: getUserById?.data?.data?.companyId,
    Persona: persona,
  };

  if (analyticsBrowser) {
    analyticsBrowser.ready(function () {
      mixpanel.init(process.env.REACT_APP_MIXPANEL_PROJECT_ID ?? '', {
        persistence: 'localStorage',
        autocapture: false,
        record_sessions_percent: 100,
        record_mask_all_text: false,
      });

      // Role is tracked as two write-only-`true` capability flags rather than a
      // single mutable field. Each role sends only its own flag (never `false`),
      // so switching modes adds the new flag without erasing the other — a
      // dual-role user ends up with both `isAdmin` and `isAdvocate` set to true.
      // Mixpanel's profile `set` only updates keys present in the payload.
      analyticsBrowser.identify(userInfo.Id, {
        name: userInfo.Name,
        email: userInfo.Email,
        session: userInfo.Session,
        company: userInfo.Company,
        persona: userInfo.Persona,
        ...(isAdminPlatform ? { isAdmin: true } : { isAdvocate: true }),
      });

      if (userInfo.CompanyId) {
        analyticsBrowser.group(userInfo.CompanyId, {
          name: userInfo.Company,
        });
      }

      if (!sessionMiddlewareRegistered) {
        sessionMiddlewareRegistered = true;
        try {
          registerSessionMiddleware(analyticsBrowser);
        } catch (e) {
          // Middleware registration is best-effort; failure here should not
          // block identify or downstream tracking.
          console.warn('Failed to register session middleware', e);
        }
      }

      ensureSession({
        userId: profile?.user_id || '',
        companyId: userInfo.CompanyId || '',
        persona: userInfo.Persona,
      });

      mixpanel.identify(userInfo.Id);

      mixpanel.people.set({
        $name: userInfo.Name,
        $email: userInfo.Email,
        Session: userInfo.Session,
        Company: userInfo.Company,
      });

      mixpanel.set_config({
        autocapture: true,
        record_sessions_percent: 100,
      });
    });
  } else {
    console.warn(
      'Segment analytics object not found. Mixpanel will not be initialized via Segment ready callback, and user will not be identified by Segment.',
    );
  }

  // add userId to AWSRum - this can remain outside if AWSRum doesn't depend on Segment/Mixpanel readiness
  if ((window as any).AWSRum?.addSessionAttributes && profile?.user_id) {
    (window as any).AWSRum.addSessionAttributes({
      userId: profile?.user_id,
    });
  }
  return;
}

/**
 * Clears analytics identity on logout. Resets Segment — which clears its cached
 * traits and anonymousId so the next user on the same browser does not inherit
 * the previous user's capability flags (isAdmin/isAdvocate) — and the direct
 * Mixpanel identity used for session replay. Also clears the local dedupe guard
 * so the next login re-identifies cleanly.
 */
export function resetIdentity(): void {
  lastIdentifyKey = null;
  try {
    analyticsBrowser?.reset();
  } catch (e) {
    console.warn('Failed to reset Segment analytics identity', e);
  }
  try {
    mixpanel.reset();
  } catch (e) {
    console.warn('Failed to reset Mixpanel identity', e);
  }
}

export default addUserProperties;
